import React, {useEffect, useMemo, useRef, useState} from "react";
import styled from "@emotion/styled";
import Logo from "../component/Logo";
import ContextMenu from "./ContextMenu";
import {dialog_type_custom, useDialogContext} from "../../../context/DialogContextProvider";
import {useMobileDetect} from "../../../utils/Hooks";
import {
    BROADCAST_ON_PASS_MOBILE_HEADER_TITLE,
    getImageUrl,
    useApplicationContext,
} from "../../../context/ApplicationContextProvider";
import {useTheme} from "@emotion/react";
import {useNavigate} from "react-router-dom";

export const HEADER_TYPE_NONE = 1;
export const HEADER_TYPE_BASE = 2; // default header type (logo--title--action)
export const HEADER_TYPE_LOGO_ONLY = 3; // default header type (--logo--)
export const HEADER_TYPE_BASE_MOBILE_LOGO_ONLY = 4; // default header type -> (mobile logo only)

export const HEADER_LOGO_TYPE_BASE = 1;

export const HEADER_MENU_TO_COMPANY = 1;
export const HEADER_MENU_TO_MAIN = 2;

const Header = ({theme, container, header}) => {
    const {is_mobile} = useMobileDetect();
    const {broadcast} = useApplicationContext();
    const {showDialog, hideDialog} = useDialogContext();
    const navigate = useNavigate();

    // state
    const [mobile_title, setMobileTitle] = useState(null);

    // DOM ref
    const ref_header_content = useRef();

    // ref
    const context_dialog_id = useRef(null);

    // css style
    const {view} = useComponentStyle(header, is_mobile);

    useEffect(() => {
        const unlisten = broadcast.listen([BROADCAST_ON_PASS_MOBILE_HEADER_TITLE], ({value}) => {
            setMobileTitle(value);
        });

        return () => {
            broadcast.unlisten(unlisten);
        };
    }, [broadcast]);

    const container_class_names = useMemo(() => {
        let class_name = `header-container`;

        if (header.type === HEADER_TYPE_LOGO_ONLY) {
            class_name += " logo_only";
        }
        if (header.with_spacing) {
            class_name += " with_spacing";
        }
        if (header.with_sticky) {
            class_name += " with_sticky";
        }

        return class_name;
    }, [header.type, header.with_spacing, header.with_sticky]);

    const showContextMenu = (header_menus) => {
        let dialog = showDialog({
            type: dialog_type_custom,
            // @ inline component ver
            component: ContextMenu,
            component_props: {
                menus: header_menus,
                anchor_view: ref_header_content.current,
            },
            on_dismiss: () => {
                context_dialog_id.current = null;
            },
            with_history: false,
            with_blocking: false,
        });
        context_dialog_id.current = dialog.id;
    };

    const hideContextMenu = () => {
        if (context_dialog_id.current > 0) {
            hideDialog(context_dialog_id.current);
        }
    };

    return (
        <view.HeaderContainer className={container_class_names}>
            {is_mobile ? (
                header.type !== HEADER_TYPE_BASE_MOBILE_LOGO_ONLY ? (
                    <>
                        <view.MobileHeaderBackButton
                            onClick={() => {
                                navigate(-1);
                            }}>
                            <img src={getImageUrl("image/common/ic_arrow_left_grey_x3.png")} alt="뒤로가기" />
                        </view.MobileHeaderBackButton>
                        <view.MobileHeaderTitle>{mobile_title}</view.MobileHeaderTitle>
                    </>
                ) : (
                    <Logo left={"3%"} header={true} />
                )
            ) : header.type === HEADER_TYPE_LOGO_ONLY ? (
                <Logo left={"46%"} header={true} />
            ) : (
                <>
                    <Logo top={"0"} header={true} />
                    <view.MyPageMenu
                        ref={ref_header_content}
                        onMouseEnter={() => {
                            if (context_dialog_id.current > 0) {
                                hideContextMenu();
                            } else {
                                showContextMenu(header.context_menus || header.menus);
                            }
                        }}
                    />
                </>
            )}
        </view.HeaderContainer>
    );
};

export default Header;

export const useComponentStyle = (header, is_mobile) => {
    const theme = useTheme();
    return {
        view: {
            HeaderContainer: styled.div({
                width: "100%",
                maxWidth: "1128px",
                margin: "auto",
                minWidth: "260px",
                height: is_mobile ? "4em" : "3em",
                boxSizing: "border-box",
                display: header.type === HEADER_TYPE_NONE ? "none" : "flex",
                justifyContent: header.type === HEADER_TYPE_LOGO_ONLY ? "center" : "space-between",
                alignItems: "center",
                position: is_mobile ? "fixed" : "relative",
                top: 0,
                zIndex: "9999",
                backgroundColor: theme.colors.w,
            }),
            MyPageMenu: styled.div({
                display: "flex",
                alignItems: "center",
                position: "absolute",
                width: "30px",
                marginRight: "10px",
                right: "0",
                height: "100%",
                cursor: "pointer",
                background: `url(${getImageUrl("image/header/ic_my_g_x2.png")}) center center / contain no-repeat`,
            }),
            MobileHeaderBackButton: styled.div({
                width: "5%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                minWidth: "1.8em",
                "& > img": {
                    width: "70%",
                },
            }),
            MobileHeaderTitle: styled.div({
                width: "95%",
                height: "100%",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontSize: theme.font_size.f19,
                fontWeight: "700",
                display: "inline-block",
                lineHeight: "64px",
                textAlign: "center",
                alignItems: "center",
                justifyContent: "center",
                transition: "all .2s",
                "@media (max-width: 320px)": {
                    fontSize: theme.font_size.f16,
                },
            }),
        },
    };
};
